import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import TiktokDateRangeFilter, { pickDatePart } from '@/tiktok-platform/components/TiktokDateRangeFilter';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

function pickData(res) {
  if (!res || typeof res !== 'object') return {};
  if (res.data && typeof res.data === 'object') return res.data;
  return res;
}

function formatNum(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isFinite(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}

function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shiftDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** 经营数据通常到昨天为止，结束日用昨天，避免查到尚未产出的日期。 */
function defaultAnalyticsRange() {
  const end = shiftDays(new Date(), -1);
  const start = shiftDays(end, -6);
  return { start: isoDate(start), end: isoDate(end) };
}

function moneyText(node) {
  if (node == null || node === '') return '—';
  if (typeof node === 'number' || typeof node === 'string') return formatNum(node);
  if (node.overall) return moneyText(node.overall);
  const amount = node.amount ?? node.value;
  if (amount == null || amount === '') return '—';
  const currency = node.currency ? ` ${node.currency}` : '';
  return `${formatNum(amount)}${currency}`;
}

function percentText(raw) {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  const value = n <= 1 ? n * 100 : n;
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function localizeApiNote(message) {
  return String(message || '').replace(
    /（平台 code=([^）]+)）/g,
    (_, code) => ` (${fixedUi('平台 code')}=${code})`,
  );
}

const BREAKDOWN_LABELS = {
  LIVE: '直播',
  VIDEO: '短视频',
  PRODUCT_CARD: '商品卡',
};

/**
 * 经营数据：进页拉取（后端 1 小时内读快照表，过期打平台并回写）。
 */
export default function TiktokAnalyticsPage() {
  const {
    loading: shopLoading,
    concreteScopeId,
    shopDimensionTabs,
  } = useTiktokPageShopScope();
  const shopId = concreteScopeId;

  const initialRange = useMemo(() => defaultAnalyticsRange(), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [shopPerf, setShopPerf] = useState(null);
  const [products, setProducts] = useState([]);
  const [insight, setInsight] = useState('sku');
  const [insightRows, setInsightRows] = useState([]);
  const [insightNote, setInsightNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncedAt, setSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();
  const syncing = isBusy('analytics', shopId);

  const rangeQuery = useMemo(() => ({
    start_date: pickDatePart(startDate),
    end_date: pickDatePart(endDate),
  }), [startDate, endDate]);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!shopId) {
      setShopPerf(null);
      setProducts([]);
      setSyncedAt('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [shopRes, productRes] = await Promise.all([
        tiktokPlatformAPI.getShopPerformance(shopId, {
          ...rangeQuery,
          force: force || undefined,
          granularity: 'ALL',
          currency: 'LOCAL',
        }),
        tiktokPlatformAPI.listProductPerformance(shopId, {
          ...rangeQuery,
          force: force || undefined,
          page_size: 20,
          sort_field: 'gmv',
          sort_order: 'DESC',
          currency: 'LOCAL',
        }),
      ]);
      const shopData = pickData(shopRes);
      const productData = pickData(productRes);
      const hasShop = shopData && (shopData.performance || shopData.latest_available_date || Object.keys(shopData).length > 0);
      const list = Array.isArray(productData?.products)
        ? productData.products
        : (Array.isArray(productData?.shop_products) ? productData.shop_products : []);
      setShopPerf(hasShop ? shopData : null);
      setProducts(list);
      setSyncedAt(String(shopRes?.syncedAt || productRes?.syncedAt || '').trim());
    } catch (e) {
      setShopPerf(null);
      setProducts([]);
      setSyncedAt('');
      setError(localizeApiNote(e?.message || fixedUi('加载经营数据失败')));
      showToast(localizeApiNote(e?.message || fixedUi('加载经营数据失败')), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, rangeQuery]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!shopId) {
      setInsightRows([]);
      setInsightNote('');
      return undefined;
    }
    let cancelled = false;
    const query = {
      start_date: pickDatePart(startDate),
      end_date: pickDatePart(endDate),
      currency: 'LOCAL',
      page_size: 20,
    };
    (async () => {
      try {
        let res;
        if (insight === 'sku') res = await tiktokPlatformAPI.listSkuPerformance(shopId, query);
        else if (insight === 'hour') res = await tiktokPlatformAPI.getPerformancePerHour(shopId, { date: pickDatePart(endDate), currency: 'LOCAL' });
        else if (insight === 'video') res = await tiktokPlatformAPI.getVideoOverview(shopId, query);
        else if (insight === 'live') res = await tiktokPlatformAPI.getLiveOverview(shopId, query);
        else res = await tiktokPlatformAPI.getSpsOverview(shopId, { locale: 'zh-CN' });
        if (cancelled) return;
        const data = pickData(res);
        const list = [
          data.skus,
          data.shop_skus,
          data.hours,
          data.performance,
          data.intervals,
        ].find(Array.isArray) || [];
        setInsightRows(list);
        setInsightNote(list.length ? '' : (data.score != null ? `${fixedUi('体验分')} ${data.score}` : ''));
      } catch (e) {
        if (cancelled) return;
        setInsightRows([]);
        setInsightNote(localizeApiNote(e?.message || fixedUi('加载失败')));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopId, startDate, endDate, insight]);

  const handleForceRefresh = async () => {
    if (!shopId || syncing) return;
    setError('');
    try {
      const track = await startJob({
        kind: 'analytics',
        shopId,
        label: fixedUi('经营数据'),
        payload: {
          start_date: pickDatePart(startDate),
          end_date: pickDatePart(endDate),
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (next) => {
          unsub();
          resolve(next);
        });
      });
      if (!result?.ok) return;
      await load({ force: false });
    } catch (e) {
      setError(localizeApiNote(e?.message || fixedUi('拉取失败')));
    }
  };

  const applyPreset = (days) => {
    const end = shiftDays(new Date(), -1);
    const start = shiftDays(end, -(days - 1));
    setStartDate(isoDate(start));
    setEndDate(isoDate(end));
  };

  const interval = shopPerf?.performance?.intervals?.[0] || null;
  const sales = interval?.sales || {};
  const traffic = interval?.traffic || {};
  const breakdowns = Array.isArray(sales?.gmv?.breakdowns) ? sales.gmv.breakdowns : [];

  const kpis = [
    { label: 'GMV', value: moneyText(sales.gmv) },
    { label: '订单数', value: formatNum(sales.orders_count) },
    { label: '客单价', value: (() => {
      const amount = Number(sales.gmv?.overall?.amount);
      const orders = Number(sales.orders_count);
      if (!Number.isFinite(amount) || !Number.isFinite(orders) || orders <= 0) return '—';
      return moneyText({ amount: amount / orders, currency: sales.gmv?.overall?.currency });
    })() },
    { label: '成交件数', value: formatNum(sales.items_sold) },
    { label: '日均买家', value: formatNum(sales.avg_customers_count) },
    { label: '退款金额', value: moneyText(sales.refunds) },
    { label: '日均访客', value: formatNum(traffic.avg_visitors) },
    { label: '日均浏览', value: formatNum(traffic.avg_page_views) },
    { label: '转化率', value: percentText(traffic.avg_conversation_rate) },
  ];

  return (
    <div className="tko-page tko-page--analytics">
      <TiktokSyncProgressSlot kinds={['analytics']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            busy={loading || shopLoading || syncing}
            syncKinds={['analytics']}
            onSync={handleForceRefresh}
            syncing={syncing}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={syncedAt ? `${fixedUi('最近拉取')}：${syncedAt}` : ''}
            syncHint={fixedUi('从 TikTok 后台拉取所选日期的店铺和商品表现，并写入本地快照。')}
          />
        )}
      >
        {shopDimensionTabs}
        {shopId ? (
          <div className="tko-filter-bar">
            <div className="tko-filter-bar__fields">
              <TiktokDateRangeFilter
                showTimeType={false}
                dateOnly
                startDate={startDate}
                endDate={endDate}
                disabled={!shopId}
                startPlaceholder={fixedUi("开始日期")}
                endPlaceholder={fixedUi("结束日期")}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
            </div>
            <div className="tko-filter-bar__actions">
              <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={() => applyPreset(7)}>{fixedUi("近 7 天")}</button>
              <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={() => applyPreset(28)}>{fixedUi("近 28 天")}</button>
              <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" disabled={!shopId || loading} onClick={() => load()}>{fixedUi("查询")}</button>
            </div>
          </div>
        ) : null}
        {!shopId && (
          <p className="tko-board-note">{fixedUi("请先选择具体店铺")}</p>
        )}
        {error && (
          <p className="tko-board-error">{localizeApiNote(error)}</p>
        )}
      </ProductFilterCard>

      <ProductListCard title={fixedUi("店铺表现")}>
        <div className="tko-board-kpi">
          {kpis.map((k) => (
            <div key={k.label} className="tko-board-kpi__card">
              <span className="tko-board-kpi__label">{fixedUi(k.label)}</span>
              <strong className="tko-board-kpi__value">{loading ? '…' : k.value}</strong>
            </div>
          ))}
        </div>
        {breakdowns.length > 0 ? (
          <div className="tko-board-chips">
            {breakdowns.map((item) => (
              <span key={item.type || item.gmv?.amount} className="tko-board-chip">
                {fixedUi(BREAKDOWN_LABELS[item.type] || item.type || '渠道')}
                <strong>{moneyText(item.gmv)}</strong>
              </span>
            ))}
          </div>
        ) : null}
      </ProductListCard>

      <ProductListCard title={fixedUi("商品表现")}>
        <div className="tko-board-table-wrap">
          <table className="tko-board-table">
            <thead>
              <tr>
                <th>{fixedUi("商品 ID")}</th>
                <th>GMV</th>
                <th>{fixedUi("订单")}</th>
                <th>{fixedUi("成交件数")}</th>
                <th>{fixedUi("曝光")}</th>
                <th>{fixedUi("点击")}</th>
                <th>{fixedUi("点击率")}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && products.length === 0 && (
                <tr>
                  <td colSpan={7} className="tko-board-table__empty">
                    {fixedUi('所选日期没有商品表现数据')}
                  </td>
                </tr>
              )}
              {products.map((row, idx) => {
                const perf = row.total_performance || row.performance || row;
                const id = row.id || row.product_id || row.productId || `row-${idx}`;
                return (
                  <tr key={String(id)}>
                    <td className="tko-board-table__code">{String(id)}</td>
                    <td>{moneyText(perf.gmv)}</td>
                    <td>{formatNum(perf.orders ?? perf.orders_count)}</td>
                    <td>{formatNum(perf.items_sold)}</td>
                    <td>{formatNum(perf.impressions ?? perf.exposure)}</td>
                    <td>{formatNum(perf.clicks ?? perf.click_count)}</td>
                    <td>{percentText(perf.click_through_rate ?? perf.ctr)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ProductListCard>

      <ProductListCard title={fixedUi("更多经营分析")}>
        <div className="tko-status-tabs" role="tablist">
          {[
            ['sku', 'SKU'],
            ['hour', '分时'],
            ['video', '短视频'],
            ['live', '直播'],
            ['sps', '体验分'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`tko-status-tab${insight === id ? ' tko-status-tab--active' : ''}`}
              onClick={() => setInsight(id)}
            >
              {fixedUi(label)}
            </button>
          ))}
        </div>
        {insightNote ? <p className="tko-board-note">{localizeApiNote(insightNote)}</p> : null}
        <div className="tko-board-table-wrap">
          <table className="tko-board-table">
            <thead>
              <tr>
                <th>{fixedUi("对象")}</th>
                <th>{fixedUi("GMV / 指标")}</th>
                <th>{fixedUi("订单")}</th>
                <th>{fixedUi("件数")}</th>
              </tr>
            </thead>
            <tbody>
              {insightRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="tko-board-table__empty">{fixedUi("当前维度没有列表数据")}</td>
                </tr>
              ) : insightRows.map((row, idx) => {
                const perf = row.total_performance || row.performance || row;
                const id = row.id || row.sku_id || row.product_id || row.hour || row.start_time || `row-${idx}`;
                return (
                  <tr key={String(id)}>
                    <td>{String(id)}</td>
                    <td>{moneyText(perf.gmv || perf.gmv_amount || row.gmv || row.value)}</td>
                    <td>{formatNum(perf.orders ?? perf.orders_count ?? perf.sku_orders)}</td>
                    <td>{formatNum(perf.units_sold ?? perf.items_sold)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ProductListCard>
    </div>
  );
}
